import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { SITE } from '@/constants/testIds';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_82895199-ac46-4908-9973-9983d7239f1a/artifacts/w46763t8_Centrale-%20Logo.png';

const Header = () => {
  const { t, lang, setLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { id: 'home', testid: SITE.navHome, label: t.nav.home, href: '/#hero' },
    { id: 'services', testid: SITE.navServices, label: t.nav.services, href: '/#services' },
    { id: 'advantages', testid: SITE.navAdvantages, label: t.nav.advantages, href: '/#advantages' },
    { id: 'process', testid: SITE.navProcess, label: t.nav.process, href: '/#process' },
    { id: 'faq', testid: SITE.navFaq, label: t.nav.faq, href: '/#faq' },
    { id: 'contact', testid: SITE.navContact, label: t.nav.contact, href: '/#contact' },
  ];

  const handleClose = () => setMobileOpen(false);

  return (
    <header
      data-testid={SITE.header}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#0B0F0C]/90 backdrop-blur-xl border-b border-white/5 shadow-[0_8px_30px_rgba(0,0,0,0.25)]'
          : 'bg-[#0B0F0C]/70 backdrop-blur-md border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-6">
          {/* Logo */}
          <a
            href="/#hero"
            data-testid={SITE.logo}
            className="flex items-center gap-3 group"
            aria-label="E-Central"
          >
            <img
              src={LOGO_URL}
              alt="E-Central"
              className="h-10 w-10 lg:h-11 lg:w-11 rounded-full ring-1 ring-white/10"
            />
            <div className="leading-tight">
              <div className="font-['Outfit'] text-white font-semibold text-lg tracking-tight">
                {t.common.brand}
                <span className="text-[#22C55E]">.</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/50">
                {t.common.brandSub}
              </div>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7" aria-label="primary">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                data-testid={l.testid}
                className="text-sm font-medium text-white/70 hover:text-white transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#22C55E] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
              data-testid={SITE.langToggle}
              aria-label="Toggle language"
              className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors border border-white/10 hover:border-[#22C55E]/60 rounded-full px-3 py-2"
            >
              <span data-testid={SITE.langFr} className={lang === 'fr' ? 'text-[#22C55E]' : ''}>FR</span>
              <span className="text-white/30">·</span>
              <span data-testid={SITE.langEn} className={lang === 'en' ? 'text-[#22C55E]' : ''}>EN</span>
            </button>

            <a
              href="/#contact"
              data-testid={SITE.ctaHeader}
              className="hidden sm:inline-flex items-center justify-center bg-[#00A651] hover:bg-[#22C55E] text-white text-sm font-semibold py-2.5 px-5 rounded-xl transition-all duration-300 hover:-translate-y-0.5 shadow-[0_8px_24px_-8px_rgba(34,197,94,0.6)]"
            >
              {t.nav.ctaHeader}
            </a>

            <button
              type="button"
              className="lg:hidden p-2 text-white/80 hover:text-white"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
              data-testid={SITE.mobileMenuToggle}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden pb-6 pt-2 border-t border-white/10">
            <nav className="flex flex-col gap-1" aria-label="mobile">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  data-testid={`mobile-${l.testid}`}
                  onClick={handleClose}
                  className="px-2 py-3 text-base text-white/80 hover:text-white border-b border-white/5"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex items-center gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
                  className="flex-1 inline-flex items-center justify-center text-xs font-semibold uppercase tracking-[0.18em] text-white/80 border border-white/10 rounded-xl py-2.5"
                >
                  {lang === 'fr' ? 'Switch to EN' : 'Passer en FR'}
                </button>
                <a
                  href="/#contact"
                  onClick={handleClose}
                  className="flex-1 inline-flex items-center justify-center bg-[#00A651] hover:bg-[#22C55E] text-white text-sm font-semibold py-2.5 px-5 rounded-xl"
                >
                  {t.nav.ctaHeader}
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
